import React, { useEffect } from 'react';

const SpaceRoomTypes = ({ data, updateData }) => {
  const handleInputChange = (field, value) => {
    updateData({ [field]: value });
  };

  const calculateFromArea = () => {
    const area = parseFloat(data.areaSqm);
    const length = parseFloat(data.lengthM);
    const width = parseFloat(data.widthM);
    
    if (area && length && !width) {
      updateData({ widthM: (area / length).toFixed(2) });
    } else if (area && width && !length) {
      updateData({ lengthM: (area / width).toFixed(2) });
    }
  };

  const calculateFromLength = () => {
    const area = parseFloat(data.areaSqm);
    const length = parseFloat(data.lengthM);
    const width = parseFloat(data.widthM);
    
    if (length && width && !area) {
      updateData({ areaSqm: (length * width).toFixed(2) });
    } else if (length && area && !width) {
      updateData({ widthM: (area / length).toFixed(2) });
    }
  };

  const calculateFromWidth = () => {
    const area = parseFloat(data.areaSqm);
    const length = parseFloat(data.lengthM);
    const width = parseFloat(data.widthM);
    
    if (length && width && !area) {
      updateData({ areaSqm: (length * width).toFixed(2) });
    } else if (width && area && !length) {
      updateData({ lengthM: (area / width).toFixed(2) });
    }
  };

  const getSpaceClassificationText = () => {
    if (!data.selectedSAC) return 'Select SAC option above';
    
    const sacMap = {
      'sac01': 'Building Element Footprint Components',
      'sac02': 'Interior Space Use Classifications (ISUC)',
      'sac03': 'Exterior Space Use Classifications (ESUC)'
    };
    
    return sacMap[data.selectedSAC] || 'Select SAC option above';
  };

  // Space type options based on selected SAC
  const getSpaceTypeOptions = () => {
    if (!data.selectedSAC) return [];
    
    const spaceTypeData = {
      sac02: {
        'IS01': 'Vertical Penetrations – Circulation',
        'IS02': 'Vertical Penetrations – Technical',
        'IS03': 'Living Spaces – Common',
        'IS04': 'Living Spaces – Private',
        'IS05': 'Amenities',
        'IS06': 'Hygiene Areas',
        'IS07': 'Circulation Areas',
        'IS08': 'Technical Services'
      },
      sac03: {
        'ES01': 'External Building Areas (Roofed)',
        'ES02': 'Exterior Spaces (Paved/Impervious)'
      }
    };
    
    return data.selectedSAC === 'sac01' ? [] : spaceTypeData[data.selectedSAC] || {};
  };

  // Room type options based on selected space type
  const getRoomTypeOptions = () => {
    if (!data.spaceType) return [];
    
    const roomTypeData = {
      'IS01': ['Main Staircase', 'Secondary Staircase', 'Fire Exit Staircase', 'Escalator'],
      'IS02': ['Elevator Shaft', 'Service Elevator', 'Plumbing Shaft', 'Electrical Shaft'],
      'IS03': ['Living Room', 'Dining Room', 'Great Room', 'Entertainment Room'],
      'IS04': ['Master Bedroom', 'Bedroom 1', 'Bedroom 2', 'Guest Room'],
      'IS05': ['Gym', 'Sauna', 'Function Room', 'Game Room'],
      'IS06': ['Master Bath', 'Common Bath', 'Powder Room', 'Shower Room'],
      'IS07': ['Main Hallway', 'Lobby', 'Foyer', 'Gallery'],
      'IS08': ['Main Kitchen', 'Service Kitchen', 'Pantry', 'Laundry'],
      'ES01': ['Covered Porch', 'Covered Patio', 'Balcony', 'Roof Deck'],
      'ES02': ['Driveway', 'Open Patio', 'Walkway', 'Courtyard']
    };
    
    return roomTypeData[data.spaceType] || [];
  };

  const spaceTypeOptions = getSpaceTypeOptions();
  const roomTypeOptions = getRoomTypeOptions();

  return (
    <div className="card p-3">
      <h3 className="font-semibold mb-3">2.2. SPACE & ROOM TYPES</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <span className="label">SPACE USE CLASSIFICATION</span>
          <div className={`input ${!data.selectedSAC ? 'bg-gray-100 text-gray-500' : 'bg-green-50 text-green-700 font-medium'} min-h-[2.5rem] flex items-center`}>
            {getSpaceClassificationText()}
          </div>
          <label className="grid gap-1 mt-2">
            <span className="label">AREA (SQM)</span>
            <input 
              className="input" 
              type="number" 
              step="0.01" 
              placeholder="0.00" 
              value={data.areaSqm || ''} 
              onChange={(e) => handleInputChange('areaSqm', e.target.value)}
              onBlur={calculateFromArea} 
            />
          </label>
        </div>
        <div>
          <label className="grid gap-1">
            <span className="label">SPACE TYPE</span>
            <select 
              className={`input ${!data.selectedSAC || data.selectedSAC === 'sac01' ? 'disabled' : ''}`} 
              disabled={!data.selectedSAC || data.selectedSAC === 'sac01'} 
              value={data.spaceType || ''}
              onChange={(e) => handleInputChange('spaceType', e.target.value)}
            >
              <option value="">
                {data.selectedSAC && data.selectedSAC !== 'sac01' 
                  ? 'Select space type' 
                  : data.selectedSAC === 'sac01' 
                    ? 'Not applicable for SAC01' 
                    : 'Select classification first'}
              </option>
              {Object.entries(spaceTypeOptions).map(([code, description]) => (
                <option key={code} value={code}>
                  {`${code} – ${description}`}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 mt-2">
            <span className="label">LENGTH (M)</span>
            <input 
              className="input" 
              type="number" 
              step="0.01" 
              placeholder="0.00" 
              value={data.lengthM || ''} 
              onChange={(e) => handleInputChange('lengthM', e.target.value)}
              onBlur={calculateFromLength} 
            />
          </label>
        </div>
        <div>
          <label className="grid gap-1">
            <span className="label">ROOM TYPE</span>
            <select 
              className={`input ${!data.spaceType ? 'disabled' : ''}`} 
              disabled={!data.spaceType} 
              value={data.roomType || ''}
              onChange={(e) => handleInputChange('roomType', e.target.value)}
            >
              <option value="">
                {data.spaceType ? 'Select room type' : 'Select space type first'}
              </option>
              {roomTypeOptions.map((roomType) => (
                <option key={roomType} value={roomType}>
                  {roomType}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 mt-2">
            <span className="label">WIDTH (M)</span>
            <input 
              className="input" 
              type="number" 
              step="0.01" 
              placeholder="0.00" 
              value={data.widthM || ''} 
              onChange={(e) => handleInputChange('widthM', e.target.value)}
              onBlur={calculateFromWidth} 
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default SpaceRoomTypes;